import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import FloatingContactButtons from '../../components/FloatingContactButtons';
import CallMeBackPopup from '../../components/CallMeBackPopup';
import tempoTravellerRoutes from '../../data/tempoTravellerRoutes';
import { calculateOneWayFare, VEHICLE_RATES_2026 } from '../../utils/pricingCalculator';

const TempoTravellerRouteBooking = () => {
  const { route } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCallMeBack, setShowCallMeBack] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');

  // Check if route exists in predefined data, otherwise use dynamic calculation
  let routeData = tempoTravellerRoutes?.[route];

  // If route not found in predefined data, create dynamic route data from location state
  if (!routeData && location?.state?.from && location?.state?.to) {
    const from = location?.state?.from;
    const to = location?.state?.to;

    // Estimate distance and toll (you can enhance this with actual distance API)
    const estimatedDistance = 200; // Default estimate
    const estimatedToll = 280;

    routeData = {
      from,
      to,
      fareStart: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.tempoTraveller12, estimatedToll),
      vehicles: [
      {
        id: 1,
        name: 'Tempo Traveller',
        seats: 12,
        luggage: 8,
        price: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.tempoTraveller12, estimatedToll),
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_17e05246a-1768446172520.png",
        imageAlt: 'Tempo Traveller 12 seater'
      },
      {
        id: 2,
        name: 'Tempo Traveller',
        seats: 17,
        luggage: 15,
        price: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.tempoTraveller17, estimatedToll),
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_19f232574-1768446172689.png",
        imageAlt: 'Tempo Traveller 17 seater'
      },
      {
        id: 3,
        name: 'Tempo Traveller',
        seats: 25,
        luggage: 15,
        price: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.tempoTraveller25 || 98, estimatedToll),
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_19c80cf8f-1768446172585.png",
        imageAlt: 'Tempo Traveller 25 seater'
      },
      {
        id: 4,
        name: 'Force Urbania',
        seats: 16,
        luggage: 16,
        price: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.urbania16 || 85, estimatedToll),
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_16387bc8f-1768524310886.png",
        imageAlt: 'Force Urbania 16 seater'
      }]

    };
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!routeData) {
      navigate('/tempo-traveller-amritsar');
    } else if (routeData?.dayOptions && routeData?.dayOptions?.length > 0) {
      setSelectedDay(routeData?.dayOptions?.[0]);
    }
  }, [routeData, navigate]);

  if (!routeData) {
    return null;
  }

  const handleBookNow = (vehicle) => {
    navigate('/car-details', {
      state: {
        from: routeData?.from,
        to: routeData?.to,
        vehicle: vehicle?.name,
        price: vehicle?.price,
        seats: vehicle?.seats,
        luggage: vehicle?.luggage,
        image: vehicle?.image,
        alt: vehicle?.imageAlt
      }
    });
  };

  return (
    <>
      <Helmet>
        <title>{`Tempo Traveller from ${routeData?.from} to ${routeData?.to} | Advait Tour and Travels`}</title>
        <meta name="description" content={`Book tempo traveller from ${routeData?.from} to ${routeData?.to}. Starting at ₹${routeData?.fareStart?.toLocaleString('en-IN')}.`} />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-grow">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white py-16">
            <div className="container mx-auto px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
                {routeData?.from} to {routeData?.to} Tempo Traveller
              </h1>
            </div>
          </div>

          {/* Day Selection Tabs */}
          {routeData?.dayOptions && routeData?.dayOptions?.length > 0 &&
          <div className="bg-white shadow-sm">
              <div className="container mx-auto px-4 py-6">
                <div className="flex flex-wrap gap-3 justify-center">
                  {routeData?.dayOptions?.map((day) =>
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`px-6 md:px-8 py-3 rounded-full font-semibold text-sm md:text-base transition-all duration-200 ${
                  selectedDay === day ?
                  'bg-yellow-400 text-gray-900 shadow-md' :
                  'bg-white text-gray-700 border border-gray-300 hover:border-yellow-400'}`
                  }>

                      {day}
                    </button>
                )}
                </div>
              </div>
            </div>
          }

          {/* Vehicle Selection */}
          <div className="container mx-auto px-4 py-12">
            <div className="space-y-6 max-w-5xl mx-auto">
              {routeData?.vehicles?.map((vehicle) =>
              <div
                key={vehicle?.id}
                className={`bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all cursor-pointer border-2 ${
                selectedVehicle?.id === vehicle?.id ? 'border-blue-500' : 'border-transparent'}`
                }
                onClick={() => setSelectedVehicle(vehicle)}>

                  <div className="flex flex-col md:flex-row">
                    {/* Vehicle Image */}
                    <div className="md:w-64 bg-gray-50 flex items-center justify-center p-6">
                      <img
                      src={vehicle?.image}
                      alt={vehicle?.imageAlt}
                      className="w-full h-auto object-contain" />

                    </div>
                    
                    {/* Vehicle Details */}
                    <div className="flex-1 p-6 flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">CAR TYPE</p>
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">
                          {vehicle?.name}
                        </h3>
                        <button className="text-green-600 font-semibold text-sm hover:underline mb-4">
                          More Details
                        </button>
                        
                        <div className="flex items-center gap-8">
                          <div className="flex items-center text-gray-700">
                            <svg className="w-6 h-6 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20 6h-3V4c0-1.11-.89-2-2-2H9c-1.11 0-2 .89-2 2v2H4c-1.11 0-2 .89-2 2v11c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zM9 4h6v2H9V4zm11 15H4V8h16v11z" />
                            </svg>
                            <div>
                              <p className="text-lg font-semibold">{vehicle?.luggage} Luggage</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center text-gray-700">
                            <svg className="w-6 h-6 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                            </svg>
                            <div>
                              <p className="text-lg font-semibold">{vehicle?.seats} Seats</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Price and CTA */}
                      <div className="mt-6 md:mt-0 md:ml-8 flex flex-col items-end justify-center">
                        <p className="text-3xl font-bold text-green-600 mb-1">
                          ₹{vehicle?.price?.toLocaleString('en-IN')}/-
                        </p>
                        <p className="text-sm text-gray-500 mb-4">Inc. of Taxes*</p>
                        
                        <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleBookNow(vehicle);
                        }}
                        className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-3 px-8 rounded-lg transition-colors shadow-md">

                          Select Car
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-white py-12">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
                Why Choose Our Tempo Traveller Service?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Well Maintained</h3>
                  <p className="text-gray-600">All vehicles are regularly serviced and maintained for your safety</p>
                </div>
                
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Experienced Drivers</h3>
                  <p className="text-gray-600">Professional and courteous drivers with years of experience</p>
                </div>
                
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Transparent Pricing</h3>
                  <p className="text-gray-600">No hidden charges - all taxes and tolls included</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gray-100 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Need Help with Booking?
              </h2>
              <p className="text-gray-600 mb-6">
                Call us at <a href={`tel:${routeData?.phone}`} className="text-blue-600 font-bold">{routeData?.phone}</a>
              </p>
              <button
                onClick={() => setShowCallMeBack(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded transition-colors">

                Request Call Back
              </button>
            </div>
          </div>
        </main>

        <Footer />
        <FloatingContactButtons onCallMeBackClick={() => setShowCallMeBack(true)} />
        {showCallMeBack &&
        <CallMeBackPopup isOpen={showCallMeBack} onClose={() => setShowCallMeBack(false)} />
        }
      </div>
    </>);


};

export default TempoTravellerRouteBooking;